import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { modifyUser } from "../../../store/thunks/userThunk";
import { fetchClinics } from "../../../store/thunks/clinicThunk";

const UserClinicInformation = ({ user = {}, onComplete }) => {
  const dispatch = useDispatch();
  const clinics = useSelector((state) => state.clinics.clinics);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("initial");
  const [editMode, setEditMode] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Local state for managing selected clinics and default location
  const [selectedClinics, setSelectedClinics] = useState(
    user.clinics ? user.clinics.map((c) => c.clinic) : []
  );
  const [defaultLocation, setDefaultLocation] = useState(
    user.defaultLocation || ""
  );

  useEffect(() => {
    dispatch(fetchClinics()).then(() => setLoading(false));
  }, [dispatch]);

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setSelectedClinics((prevSelected) =>
      checked
        ? [...prevSelected, value]
        : prevSelected.filter((id) => id !== value)
    );

    // If a clinic is unchecked and it was the default location, clear the default location
    if (!checked && value === defaultLocation) {
      setDefaultLocation("");
    }
  };

  const handleDefaultLocationChange = (e) => {
    const { value } = e.target;
    // Only allow setting the default location if the clinic is already selected
    if (selectedClinics.includes(value)) {
      setDefaultLocation(value);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaveStatus("saving");

    // Ensure defaultLocation is either a valid ObjectId or null
    const updatedClinicianData = {
      clinics: selectedClinics.map((clinicId) => ({
        clinic: clinicId,
        isDefault: clinicId === defaultLocation,
      })),
      defaultLocation: defaultLocation || null,
    };

    dispatch(
      modifyUser({
        userId: user._id || user.userId,
        userData: updatedClinicianData,
      })
    )
      .then((response) => {
        setSaveStatus("saved");
        setEditMode(false);
        onComplete(response.payload);
        
        // Show success modal
        setShowSuccessModal(true);
        
        setTimeout(() => {
          setSaveStatus("initial");
          setShowSuccessModal(false);
        }, 2000);
      })
      .catch((error) => {
        console.error("Error saving clinician information:", error);
        setSaveStatus("error");
      });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-32">
      <div className="text-sm text-gray-600">Loading clinics...</div>
    </div>
  );

  const getClinicName = (clinicId) => {
    const clinic = clinics.find(c => c._id === clinicId);
    return clinic ? clinic.name : 'Unknown Clinic';
  };

  return (
    <div className="space-y-6">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Success</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Clinic information updated successfully.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with Edit Toggle */}
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Clinic Information</h2>
          <p className="text-sm text-gray-500 mt-1">Manage clinic assignments and default location</p>
        </div>
        <button
          type="button"
          onClick={() => setEditMode(!editMode)}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          {editMode ? 'Cancel' : 'Edit'}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* View Mode */}
        {!editMode && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Assigned Clinics</h3>
              {selectedClinics.length > 0 ? (
                <div className="space-y-2">
                  {selectedClinics.map((clinicId) => (
                    <div key={clinicId} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-sm">
                      <span className="text-sm text-gray-900">{getClinicName(clinicId)}</span>
                      {clinicId === defaultLocation && (
                        <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-gray-900 text-white">
                          Default Location
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 p-3 bg-gray-50 border border-gray-200 rounded-sm">
                  No clinics assigned
                </div>
              )}
            </div>
          </div>
        )}

        {/* Edit Mode */}
        {editMode && (
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Select Clinics</h3>
            <div className="space-y-3">
              {clinics.map((clinic) => (
                <div key={clinic._id} className="flex items-center justify-between p-3 border border-gray-200 rounded-sm hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`clinic-${clinic._id}`}
                        value={clinic._id}
                        checked={selectedClinics.includes(clinic._id)}
                        onChange={handleCheckboxChange}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                      />
                      <label htmlFor={`clinic-${clinic._id}`} className="ml-3 text-sm font-medium text-gray-900">
                        {clinic.name}
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="defaultLocation"
                      value={clinic._id}
                      checked={defaultLocation === clinic._id}
                      onChange={handleDefaultLocationChange}
                      disabled={!selectedClinics.includes(clinic._id)}
                      className={`h-4 w-4 ${
                        selectedClinics.includes(clinic._id)
                          ? "text-gray-900 focus:ring-gray-900 border-gray-300"
                          : "text-gray-300 cursor-not-allowed"
                      }`}
                    />
                    <label className="text-xs text-gray-500">
                      Default
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save Button */}
        {editMode && (
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button
              type="submit"
              disabled={saveStatus === "saving"}
              className={`inline-flex justify-center px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                saveStatus === "saving"
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : saveStatus === "saved"
                  ? "bg-green-600 text-white"
                  : saveStatus === "error"
                  ? "bg-red-600 text-white"
                  : "bg-gray-900 text-white hover:bg-gray-800"
              }`}
            >
              {saveStatus === "saving"
                ? "Saving..."
                : saveStatus === "saved"
                ? "Saved!"
                : saveStatus === "error"
                ? "Error"
                : "Save Changes"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default UserClinicInformation;
